import React from 'react'
import { Button, Cell, Image, Paragraph, Row, Separator, FlowGrid } from '@fs/zion-ui'
import { useTranslation } from 'react-i18next'
import { baseUrl } from '@fs/zion-config'
import useInfoSheetOrientationCachedState from '../../../../../shared/hooks/useInfoSheetOrientationCachedState'
import useIsHeaderHidden from '../../../../../shared/hooks/useIsHeaderHidden'
import noNamesImg from '../../../../images/no-names.svg'

/**
 * SignInBlankState
 *
 * Displays a blank state UI for signed-out users, prompting them to sign in to view indexing information.
 * Shows an illustration, message, and sign-in/sign-up buttons (when header is hidden).
 * Layout adapts based on bottom sheet vs side sheet orientation.
 *
 * @component
 * @returns {JSX.Element}
 */
const SignInBlankState = () => {
  const [t] = useTranslation()
  const { isBottomSheet } = useInfoSheetOrientationCachedState()
  const isHeaderHidden = useIsHeaderHidden()

  const signInUrl = `${baseUrl}/auth/familysearch/login?returnUrl=${encodeURIComponent(window.location)}`
  const signUpUrl = `${baseUrl}/auth/familysearch/login?option=createaccount&returnUrl=${encodeURIComponent(window.location)}`

  return (
    <FlowGrid orientation="vertical" columnCount={1} gutters="xs">
      <Separator size="nano" />
      <Row alignX="center">
        <Cell>
          <Image src={noNamesImg} alt="" width="auto" />
        </Cell>
      </Row>
      <Paragraph size="sm" centered>
        {t('select-person.subheading.need-to-login', 'Sign in to view indexing information.')}
      </Paragraph>
      {isHeaderHidden && (
        <>
          <Separator size="xs" />
          {isBottomSheet ? (
            <Row alignX="center" spacing="sm">
              <Button
                emphasis="high"
                external
                to={signInUrl}
                data-testid="sign-in-button"
                linkName="Sign In from Blank State"
              >
                {t('common-ui:sign-in.action', 'Sign In')}
              </Button>
              <Button
                emphasis="medium"
                external
                to={signUpUrl}
                data-testid="sign-up-button"
                linkName="Sign Up from Blank State"
              >
                {t('select-person.sign-up.action', 'Sign Up')}
              </Button>
            </Row>
          ) : (
            <>
              <Button
                emphasis="high"
                fullWidth
                external
                to={signInUrl}
                data-testid="sign-in-button"
                linkName="Sign In from Blank State"
              >
                {t('common-ui:sign-in.action', 'Sign In')}
              </Button>
              <Separator size="xxs" />
              <Button
                emphasis="medium"
                fullWidth
                external
                to={signUpUrl}
                data-testid="sign-up-button"
                linkName="Sign Up from Blank State"
              >
                {t('select-person.sign-up.action', 'Sign Up')}
              </Button>
            </>
          )}
        </>
      )}
    </FlowGrid>
  )
}

export default SignInBlankState
